import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const paths: Record<string, ReactNode> = {
  home: <><path d="m3 10 9-7 9 7v10H3Z"/><path d="M9 20v-7h6v7"/></>,
  dumbbell: <><path d="m7 7 10 10M5 3 3 5l4 4 2-2ZM17 15l-2 2 4 4 2-2ZM9 3l-6 6M21 15l-6 6"/></>,
  layers: <><path d="m12 3 10 5-10 5L2 8ZM2 12l10 5 10-5M2 16l10 5 10-5"/></>,
  chart: <><path d="M4 3v17h17M8 15v-4M13 15V7M18 15V4"/></>,
  history: <><path d="M3 10a9 9 0 1 1 1 7M3 4v6h6M12 7v5l3 2"/></>,
  settings: <><path d="M4 7h16M4 17h16"/><circle cx="9" cy="7" r="3"/><circle cx="16" cy="17" r="3"/></>,
  arrow: <path d="M4 12h16m-6-6 6 6-6 6"/>,
  plus: <path d="M12 5v14M5 12h14"/>,
  check: <path d="m5 12 4 4L19 6"/>,
  close: <path d="m6 6 12 12M6 18 18 6"/>,
  search: <><circle cx="10" cy="10" r="6"/><path d="m15 15 6 6"/></>,
  clock: <><circle cx="12" cy="12" r="9"/><path d="M12 6v6l4 2"/></>,
  play: <path d="m8 4 12 8-12 8Z"/>,
  pause: <><path d="M7 5v14M17 5v14"/></>,
  trophy: <><path d="M7 3h10v7a5 5 0 0 1-10 0ZM7 5H3v3a4 4 0 0 0 4 4M17 5h4v3a4 4 0 0 1-4 4M12 15v6M7 21h10"/></>,
  flame: <path d="M13 2c2 6-5 7-3 12 1-2 3-3 4-4 6 5 4 12-2 12C2 22 2 13 7 8c0 4 3 1 6-6Z"/>,
  calendar: <><rect x="3" y="5" width="18" height="16" rx="3"/><path d="M7 2v6M17 2v6M3 11h18M8 15h2M14 15h2"/></>,
  spark: <path d="m12 2 3 7 7 3-7 3-3 7-3-7-7-3 7-3Z"/>,
  book: <><path d="M12 5v16M3 3c4 0 7 0 9 2 2-2 5-2 9-2v16c-4 0-7 0-9 2-2-2-5-2-9-2Z"/></>,
  edit: <><path d="m15 4 5 5M4 20l5-1L21 7l-5-5L4 14ZM4 20h16"/></>,
  shield: <><path d="m12 2 9 4v6c0 5-9 10-9 10S3 17 3 12V6Z"/><path d="m8 12 3 3 5-6"/></>,
};
export function Icon({ name, size = 20 }: { name: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name] ?? paths.spark}</svg>;
}
let openDialogs = 0;
let originalOverflow = '';
export function Modal({ title, children, onClose, wide = false }: { title: string; children: ReactNode; onClose: () => void; wide?: boolean }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const close = useRef(onClose); close.current = onClose;
  useEffect(() => {
    const element = dialog.current;
    const before = document.activeElement as HTMLElement | null;
    element?.showModal();
    if(openDialogs===0)originalOverflow=document.body.style.overflow;
    openDialogs++;
    document.body.style.overflow = 'hidden';
    return () => { element?.close();openDialogs--;if(openDialogs===0)document.body.style.overflow=originalOverflow;if(before?.isConnected)before.focus(); };
  }, []);
  return createPortal(<dialog ref={dialog} className={`modal ${wide ? 'modal-wide' : ''}`} aria-label={title}
    onCancel={e => { e.preventDefault(); close.current(); }}
    onClick={e => { if (e.target === dialog.current) { const r = dialog.current!.getBoundingClientRect(); if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) close.current(); } }}>
    <div className="modal-header"><h2>{title}</h2><button className="icon-btn" onClick={onClose} aria-label={`Tutup ${title}`}><Icon name="close"/></button></div>
    <div className="modal-content">{children}</div>
  </dialog>, document.body);
}
export function Meter({ value, max, label }: { value: number; max: number; label: string }) {
  return <div className="meter" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={max} aria-valuenow={Math.min(max, value)}><span style={{width: `${Math.max(0, Math.min(100,value/max*100))}%`}}/></div>;
}
export function EmptyState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return <div className="empty-state"><span className="empty-icon"><Icon name="layers" size={28}/></span><h3>{title}</h3><p>{children}</p>{action}</div>;
}
export function PageTitle({ eyebrow, title, children, action }: { eyebrow: string; title: string; children?: ReactNode; action?: ReactNode }) {
  return <div className="page-title"><div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1>{children && <p className="subtext">{children}</p>}</div>{action}</div>;
}
